import React, { useState } from "react";
import { Form, Field, FormikHelpers } from "formik";
import { Grid, Box, Typography, Checkbox, FormControlLabel } from "@mui/material";

import { FormCheckbox, FormInput } from "../../../../components/ui";
import { INewRithmicAccountData } from "../../../../api/rithmic/index.types";

import FormContext from "../FormContext";
import PasswordInput from "./components/PasswordInput";
import MicrosCheckBox from "./components/MicrosCheckBox";
import ExchangesSelect from "./components/ExchangesSelect";
import SubmitFormButton from "./components/SubmitFormButton";
import StateAutocomplete from "./components/StateAutocomplete";
import AccountTypeSelect from "./components/AccountTypeSelect";
import CountryAutocomplete from "./components/CountryAutocomplete";
import RithmicUsernameInput from "./components/RithmicUsernameInput";
import ProvinceAutocomplete from "./components/ProvinceAutocomplete";
import FundingLocationSelect from "./components/FundingLocationSelect";
import FormEmailAutocomplete from "../../../../components/ui/FormEmailAutocomplete";
import OriginalAccountTypeSelect from "./components/OriginalAccountTypeSelect";
import CreateAccountFormContainer from "./components/CreateAccountFormContainer";
import AvailableAssetsTransferList from "./components/AvailableAssetsTransferList";

export interface INewRithmicAccountFormData {
  formValues: INewRithmicAccountData;
  form: FormikHelpers<INewRithmicAccountData>;
}

const CreateAccountForm: React.FC = () => {
  const [selectedAccountID, setSelectedAccountId] = useState<number | null>(null);

  return (
    <div>
      <CreateAccountFormContainer>
        {({ values, setFieldValue, isValid }, { isPending }) => {
          return (
            <Form>
              <FormContext selectedAccountID={selectedAccountID} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">EDX Account Creation</Typography>
                </Grid>
                <Grid item xs={3}>
                  <FundingLocationSelect />
                </Grid>
                <Grid item xs={3}>
                  <OriginalAccountTypeSelect values={values} />
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle1">User</Typography>
                </Grid>
                <Grid item xs={3}>
                  <FormControlLabel
                    control={<Checkbox checked={!values.existing} disabled />}
                    label="New user"
                  />
                </Grid>
                <Grid item xs={4}>
                  <Field
                    name="eod_trailing"
                    component={FormCheckbox}
                    label="EOD Trailing Account Type"
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography>
                    Rithmic Account Name: {values.account_name && <b>{values.account_name}</b>}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  {values.existing && (
                    <Typography color="warning.main">
                      Rithmic username <b>{values.rithmic}</b> already exists in Helios!
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={3}>
                  <RithmicUsernameInput />
                </Grid>
                <Grid item xs={3}>
                  <Field name="first" component={FormInput} label="First Name" />
                </Grid>{" "}
                <Grid item xs={3}>
                  <Field name="last" component={FormInput} label="Last Name" />
                </Grid>
                <Grid item xs={3}>
                  <FormEmailAutocomplete
                    setFieldValue={setFieldValue}
                    setSelectedAccountId={setSelectedAccountId}
                  />
                </Grid>
                <Grid item xs={3}>
                  <Field name="address1" component={FormInput} label="Address 1" disabled />
                </Grid>
                <Grid item xs={3}>
                  <Field name="address2" component={FormInput} label="Address 2" disabled />
                </Grid>
                <Grid item xs={3}>
                  <Field name="city" component={FormInput} label="City" disabled />
                </Grid>
                <Grid item xs={3}>
                  <PasswordInput password={values.password} setFieldValue={setFieldValue} />
                </Grid>
                <Grid item xs={3}>
                  <CountryAutocomplete />
                </Grid>
                <Grid item xs={3}>
                  <Field type="number" name="zip" component={FormInput} label="Postal" disabled />
                </Grid>
                <Grid
                  item
                  xs={3}
                  sx={values.country === "United States" ? {} : { display: "none" }}
                >
                  {values.country === "United States" && (
                    <StateAutocomplete disabled={values.isDisabledState} />
                  )}
                </Grid>
                <Grid item xs={3} sx={values.country === "Canada" ? {} : { display: "none" }}>
                  {values.country === "Canada" && (
                    <ProvinceAutocomplete disabled={values.isDisabledState} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Configuration</Typography>
                </Grid>
                <Grid item xs={6}>
                  <AvailableAssetsTransferList setFieldValue={setFieldValue} />
                </Grid>
                <Grid item xs={3}>
                  <AccountTypeSelect setFieldValue={setFieldValue} values={values} />
                  <Box sx={{ mt: "20px" }}>
                    <MicrosCheckBox />
                  </Box>
                </Grid>
                <Grid item xs={3}>
                  <ExchangesSelect />
                </Grid>
                <Grid item xs={12}>
                  <SubmitFormButton loading={isPending} disabled={!isValid} />
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </CreateAccountFormContainer>
    </div>
  );
};

export default CreateAccountForm;
